/**
 * `heysegment videos` — upload and list workspace videos.
 *
 *   heysegment videos upload <file...>   → upload; prints the public URL
 *   heysegment videos list               → the workspace's videos as markdown
 *
 * The workspace is never named on the wire — the server derives it from the
 * authenticated session. Videos are stored raw (no optimize / re-encode / AI
 * description): the CLI presigns → PUTs the file to R2 → registers the videos
 * row. `--title` / `--description` are the agent's metadata, stored verbatim;
 * omit them to leave the video untitled ("if it doesn't know, leave it blank").
 *
 * @module
 */
import { Console, Effect, FileSystem, Option } from "effect"
import { Argument, Command, Flag } from "effect/unstable/cli"
import { FetchHttpClient } from "effect/unstable/http"
import type { CliVideo } from "@repo/contracts/rpc/procedures/session"
import { withSession, step } from "../session/with-session"
import { OperationFailed, UsageError } from "../errors"
import { SessionClient } from "../rpc"
import * as transfer from "../session/transfer"
import { mimeFromVideoPath, VIDEO_EXTENSIONS_LABEL } from "../videos/detect"
import { baseName, formatBytes } from "../format"
import { apiUrlFlag, dirFlag, jsonFlag } from "./flags"

// ── Limits ────────────────────────────────────────────────────────────────

/** Client-side hard cap — matches the web upload's limit. Rejected before PUT. */
const MAX_VIDEO_BYTES = 500 * 1024 * 1024
const MAX_VIDEO_LABEL = "500 MB"

// ── Result shape ────────────────────────────────────────────────────────

export interface UploadedVideo {
  readonly file: string
  readonly id: string
  readonly type: string
  readonly url: string
  readonly title: string | null
  readonly description: string | null
  readonly fileSize: number | null
}

// ── Validation ──────────────────────────────────────────────────────────

/**
 * Validate the metadata flags. Returns a human error reason, or null when the
 * combination is valid. Metadata is per-video, so it only applies to a single
 * file. Unlike images there is no auto-describer, so a description without a
 * title is fine (both are stored verbatim, independently).
 */
export const validateUploadArgs = (input: {
  readonly fileCount: number
  readonly title: string | undefined
  readonly description: string | undefined
}): string | null => {
  if (
    (input.title !== undefined || input.description !== undefined) &&
    input.fileCount > 1
  ) {
    return "--title / --description apply to a single video. Upload one file at a time to set them."
  }
  return null
}

// ── Formatting ──────────────────────────────────────────────────────────

export const formatUploadedLine = (r: UploadedVideo): string => {
  const title = r.title !== null && r.title.length > 0 ? ` · "${r.title}"` : ""
  return `✓ ${r.file} → ${r.url}  (${formatBytes(r.fileSize)})${title}`
}

const escapeAttribute = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")

const mimeFromStoredType = (type: string): string =>
  type.startsWith("video/") ? type : (mimeFromVideoPath(`file.${type}`) ?? `video/${type}`)

export const videoEmbedSnippet = (input: {
  readonly url: string
  readonly type: string
}): string =>
  [
    "<video controls>",
    `  <source src="${escapeAttribute(input.url)}" type="${escapeAttribute(mimeFromStoredType(input.type))}" />`,
    "</video>",
  ].join("\n")

const indentSnippet = (snippet: string): string =>
  snippet
    .split("\n")
    .map((line) => `  ${line}`)
    .join("\n")

export const renderVideoList = (videos: ReadonlyArray<CliVideo>): string => {
  const lines: Array<string> = [
    `${videos.length} video${videos.length === 1 ? "" : "s"} in this workspace. Reference any by its URL in a \`<video>\` tag (with a nested \`<source src>\`).`,
    "",
  ]
  for (const vid of videos) {
    const title = vid.title ?? "(untitled)"
    const desc =
      vid.description !== null && vid.description.length > 0
        ? ` — ${vid.description}`
        : ""
    lines.push(`- **${title}**${desc}`)
    lines.push(`  ${vid.url}`)
    lines.push(indentSnippet(videoEmbedSnippet(vid)))
  }
  return lines.join("\n")
}

// ── Core: upload one file ───────────────────────────────────────────────

const uploadOne = (
  file: string,
  meta: { readonly title: string | undefined; readonly description: string | undefined },
): Effect.Effect<
  UploadedVideo,
  OperationFailed | UsageError,
  SessionClient | FileSystem.FileSystem
> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const client = yield* SessionClient

    const mimeType = mimeFromVideoPath(file)
    if (mimeType === null) {
      return yield* Effect.fail(
        new UsageError({
          reason: `Not a recognized video file: ${file} (expected ${VIDEO_EXTENSIONS_LABEL}).`,
        }),
      )
    }

    // Reject oversized files before uploading anything.
    const info = yield* step(`videos:stat ${baseName(file)}`, fs.stat(file))
    const size = Number(info.size)
    if (size > MAX_VIDEO_BYTES) {
      return yield* Effect.fail(
        new UsageError({
          reason: `${baseName(file)} is ${formatBytes(size)}, over the ${MAX_VIDEO_LABEL} limit.`,
        }),
      )
    }

    const presigned = yield* step(
      `videos:presign ${baseName(file)}`,
      client.presignVideoUpload({ mimeType, fileName: baseName(file) }),
    )

    yield* step(
      `videos:upload ${baseName(file)}`,
      transfer
        .upload({ url: presigned.url, fromFile: file, contentType: mimeType })
        .pipe(Effect.provide(FetchHttpClient.layer)),
    )

    const confirmed = yield* step(
      `videos:register ${baseName(file)}`,
      client.registerVideoUpload({
        videoId: presigned.videoId,
        type: presigned.type,
        title: meta.title,
        description: meta.description,
      }),
    )

    // Best-effort telemetry — never fail the upload on a reporting hiccup.
    yield* client
      .report({ type: "video.uploaded", data: { id: confirmed.id } })
      .pipe(Effect.ignore)

    const uploaded: UploadedVideo = {
      file,
      id: confirmed.id,
      type: confirmed.type,
      url: confirmed.url,
      title: confirmed.title,
      description: confirmed.description,
      fileSize: confirmed.fileSize,
    }
    return uploaded
  })

// ── Core: the `upload` program (testable without withSession) ───────────

export const runUpload = (input: {
  readonly files: ReadonlyArray<string>
  readonly title: Option.Option<string>
  readonly description: Option.Option<string>
  readonly json: boolean
}): Effect.Effect<
  void,
  OperationFailed | UsageError,
  SessionClient | FileSystem.FileSystem
> =>
  Effect.gen(function* () {
    const title = Option.getOrUndefined(input.title)
    const description = Option.getOrUndefined(input.description)

    const invalid = validateUploadArgs({
      fileCount: input.files.length,
      title,
      description,
    })
    if (invalid !== null) {
      return yield* Effect.fail(new UsageError({ reason: invalid }))
    }

    const results: Array<UploadedVideo> = []
    for (const file of input.files) {
      const uploaded = yield* uploadOne(file, { title, description })
      results.push(uploaded)
    }

    // Videos are never auto-described; flag any left untitled so the agent
    // knows it can name them (the app shows them as "Untitled" otherwise).
    const untitled = results.filter(
      (r) => r.title === null || r.title.length === 0,
    ).length
    const notes: Array<string> = []
    if (untitled > 0) {
      notes.push(
        `${untitled} video${untitled === 1 ? " has" : "s have"} no title — pass --title / --description at upload to name them (videos are not auto-described).`,
      )
    }

    if (input.json) {
      yield* Console.log(JSON.stringify({ ok: true, videos: results, notes }, null, 2))
      return
    }
    for (const r of results) {
      yield* Console.log(formatUploadedLine(r))
      yield* Console.log(indentSnippet(videoEmbedSnippet(r)))
    }
    for (const note of notes) {
      yield* Console.log(`\nℹ ${note}`)
    }
  })

// ── Core: the `list` program ────────────────────────────────────────────

export const runList = (input: {
  readonly json: boolean
}): Effect.Effect<void, OperationFailed, SessionClient> =>
  Effect.gen(function* () {
    const client = yield* SessionClient
    const { videos } = yield* step("videos:list", client.listVideos(void 0))

    if (input.json) {
      yield* Console.log(JSON.stringify({ ok: true, videos }, null, 2))
      return
    }
    if (videos.length === 0) {
      yield* Console.log(
        "No videos in this workspace yet. Add one with:\n" +
          "  heysegment videos upload <file>                                # stored with no title/description\n" +
          '  heysegment videos upload <file> --title "…" --description "…"  # your metadata, stored verbatim',
      )
      return
    }
    yield* Console.log(renderVideoList(videos))
  })

// ── Flags / args ────────────────────────────────────────────────────────

const filesArg = Argument.file("file", { mustExist: true }).pipe(
  Argument.withDescription(`Path to a video file (${VIDEO_EXTENSIONS_LABEL})`),
  Argument.variadic({ min: 1 }),
)

const titleFlag = Flag.string("title").pipe(
  Flag.withDescription(
    "Video title / name (single file). Stored verbatim. Omit it to leave the video untitled — videos are never auto-described.",
  ),
  Flag.optional,
)

const descriptionFlag = Flag.string("description").pipe(
  Flag.withDescription(
    "Video description (single file). Stored verbatim. Omit it to leave it blank.",
  ),
  Flag.optional,
)

// ── Commands ────────────────────────────────────────────────────────────

export const videoUploadCommand = Command.make(
  "upload",
  {
    file: filesArg,
    title: titleFlag,
    description: descriptionFlag,
    json: jsonFlag,
    dir: dirFlag,
    apiUrl: apiUrlFlag,
  },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      runUpload({
        files: flags.file,
        title: flags.title,
        description: flags.description,
        json: flags.json,
      }),
    ),
).pipe(
  Command.withDescription(
    `Upload video(s) up to ${MAX_VIDEO_LABEL}, printing the public URL to reference in a <video> tag`,
  ),
)

export const videoListCommand = Command.make(
  "list",
  { json: jsonFlag, dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      runList({ json: flags.json }),
    ),
).pipe(Command.withDescription("List the workspace's videos (title, public URL)"))

export const videosCommand = Command.make("videos").pipe(
  Command.withDescription("Upload and manage workspace videos"),
  Command.withSubcommands([videoUploadCommand, videoListCommand]),
)
